<script setup>
import { computed, ref, onMounted, defineProps, defineEmits } from "vue";
import { UsersIcon } from "@heroicons/vue/outline";

import { useCookies } from "vue3-cookies";
import { useAuth } from "@websanova/vue-auth/src/v3.js";
import { useAuthStore } from "@/stores";
import { getRoles } from "@/composable/users/getRoles.js";

const emit = defineEmits(["update:modelValue"]);

defineProps({
  modelValue: {
    default: false,
    type: Boolean,
  },
  active: {
    default: false,
    type: Boolean,
  },
});

const authStore = useAuthStore();
const auth = useAuth();
const roles = getRoles();
const isSuperAdmin = roles.isSuperAdmin.value;
const isAccountManager = roles.isAccountManager.value;
const isSpecificManager = roles.isSpecificManager.value;
const isMultiStoreManager = roles.isMultiStoreManager.value;

const { cookies } = useCookies();

const isImpersonating = ref(false);
const impersonatedUser = ref(null);

const impersonatedUserName = computed(() => impersonatedUser.value?.name);

const openModal = () => {
  emit("update:modelValue", true);
};

const stopImpersonating = () => {
  localStorage.removeItem("impersonate_user");
  localStorage.removeItem("impersonate_token");

  let token = JSON.parse(localStorage.getItem("access_token"));
  let user = JSON.parse(localStorage.getItem("user"));

  cookies.set("default_auth_token", token);
  cookies.set("default_auth_user", user.id);
  auth.user(user);
  auth.token(null, token, false);
  authStore.$patch({
    user: user,
    token: token,
  });

  window.location.reload();
};

onMounted(() => {
  const stored = localStorage.getItem("impersonate_user");
  if (stored) {
    isImpersonating.value = true;
    impersonatedUser.value = JSON.parse(stored);
  }
});
</script>
<template>
  <div>
    <button
      v-if="
        !isImpersonating &&
        (isSuperAdmin ||
          isAccountManager ||
          isSpecificManager ||
          isMultiStoreManager)
      "
      type="button"
      :class="[
        active ? 'bg-gray-50' : '',
        'flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 w-full text-left',
      ]"
      @click="openModal"
    >
      <UsersIcon class="h-4 w-4 text-gray-400 shrink-0" aria-hidden="true" />
      Switch User
    </button>

    <button
      v-if="isImpersonating"
      type="button"
      :class="[
        active ? 'bg-amber-50' : 'bg-amber-50/60',
        'flex items-center gap-2.5 px-4 py-2 text-sm w-full text-left',
      ]"
      @click="stopImpersonating"
    >
      <UsersIcon class="h-4 w-4 text-amber-500 shrink-0" aria-hidden="true" />
      <div class="min-w-0">
        <p class="font-medium text-amber-700 leading-tight">Switch back</p>
        <p v-if="impersonatedUserName" class="text-xs text-amber-600 truncate">
          Viewing as {{ impersonatedUserName }}
        </p>
      </div>
    </button>
  </div>
</template>
